import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RichTextEditor from "@/components/RichTextEditor";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Cake, Camera, Compass, ContactRound, MapPin, Phone, Plus, Save, Search, Target, Trash2, UserRound, Pencil, X, CalendarClock } from "lucide-react";
import OrgStructureChart from "@/components/OrgStructureChart";

const emptyForm = { name: "", role: "", phone: "", profile_picture: "" };
const EMPTY_FACILITY = { name: "", location: "", description: "", photos: [] };
const EMPTY_POLICIES = { rules: [], workscope: [], workflow: [], vision: "", mission: "" };
const RULE_CATEGORIES = [
  "Outing",
  "Balik bermalam",
  "Makan",
  "Solat",
  "Pakaian",
  "Barang larangan",
  "Kelas/Prep",
  "Riadah",
  "Tetamu",
  "Asrama",
  "Dewan makan",
  "Rumah warden",
  "Pondok pengawal",
  "Adab",
  "Lain-lain",
];
const WORKSCOPE_ROLES = [
  "Principal",
  "Hostel Supervisor",
  "Dining Hall Supervisor",
  "Warden",
  "Guard",
  "General Worker",
];
const POLICY_SECTIONS = [
  { id: "rules", title: "Rules", empty: "No rules have been added yet." },
  { id: "workscope", title: "Workscope", empty: "No workscope entries have been added yet." },
  { id: "workflow", title: "Workflow", empty: "No workflow entries have been added yet." },
];
const MAX_PROFILE_PICTURE_BYTES = 3 * 1024 * 1024;

const readImageFile = (file) => new Promise((resolve, reject) => {
  if (!file.type.startsWith("image/")) {
    reject(new Error("Choose an image file"));
    return;
  }
  if (file.size > MAX_PROFILE_PICTURE_BYTES) {
    reject(new Error("Photo must not exceed 3 MB"));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("Unable to read this photo"));
  reader.readAsDataURL(file);
});

const DAY_STYLE = {
  AHAD: "bg-fuchsia-100 text-fuchsia-800",
  ISNIN: "bg-orange-100 text-orange-800",
  SELASA: "bg-yellow-100 text-yellow-800",
  RABU: "bg-green-100 text-green-800",
  KHAMIS: "bg-sky-100 text-sky-800",
  JUMAAT: "bg-blue-100 text-blue-800",
  SABTU: "bg-rose-100 text-rose-800",
};
export default function OrgChart() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [facilityForm, setFacilityForm] = useState(EMPTY_FACILITY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingFacility, setSavingFacility] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [editingFacilityId, setEditingFacilityId] = useState(null);
  const [roster, setRoster] = useState(null);
  const [section, setSection] = useState("contacts");
  const [policies, setPolicies] = useState(EMPTY_POLICIES);
  const [policyFilters, setPolicyFilters] = useState({ rules: "all", workscope: "all", workflow: "all" });
  const [policyEditing, setPolicyEditing] = useState(null);
  const [policyDraft, setPolicyDraft] = useState({ category: "", content: "" });
  const [policySaving, setPolicySaving] = useState(false);
  const [visionMissionEditing, setVisionMissionEditing] = useState(false);
  const [visionMissionDraft, setVisionMissionDraft] = useState({ vision: "", mission: "" });
  const [rosterEditing, setRosterEditing] = useState(false);
  const [rosterDraft, setRosterDraft] = useState(null);
  const [rosterSaving, setRosterSaving] = useState(false);

  const canManageContacts = user?.role === "admin" || user?.staff_role === "Hostel Supervisor";
  const monthName = new Date().toLocaleString("en-US", { month: "long" });

  const loadDirectory = async () => {
    setLoading(true);
    try {
      const [directoryResponse, birthdayResponse, rosterResponse, facilityResponse, policyResponse] = await Promise.all([
        api.get("/org-chart/contacts"),
        api.get("/birthdays"),
        api.get("/roster"),
        api.get("/org-chart/facilities"),
        api.get("/rules-responsibilities"),
      ]);
      setContacts(directoryResponse.data || []);
      setBirthdays(birthdayResponse.data || []);
      setRoster(rosterResponse.data || null);
      setFacilities(facilityResponse.data || []);
      setPolicies({
        rules: policyResponse.data?.rules || [],
        workscope: policyResponse.data?.workscope || policyResponse.data?.responsibilities || [],
        workflow: policyResponse.data?.workflow || [],
        vision: policyResponse.data?.vision || "",
        mission: policyResponse.data?.mission || "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load the contact directory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDirectory();
  }, []);

  const updateRegisteredPhoto = async (event, contact) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const profilePicture = await readImageFile(file);
      await api.put(`/org-chart/users/${contact.id}/photo`, { profile_picture: profilePicture });
      setContacts((current) => current.map((entry) => (
        entry.id === contact.id ? { ...entry, profile_picture: profilePicture } : entry
      )));
      toast.success(`${contact.name}'s photo updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Unable to update photo");
    }
  };

  const addContact = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.role.trim()) {
      toast.error("Name and role are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role.trim(),
        phone: form.phone.trim(),
        profile_picture: form.profile_picture || null,
      };
      if (editingContactId) {
        await api.put(`/org-chart/contacts/${editingContactId}`, payload);
      } else {
        await api.post("/org-chart/contacts", payload);
      }
      setForm(emptyForm);
      setEditingContactId(null);
      await loadDirectory();
      toast.success(editingContactId ? "Contact updated" : "Contact added to the directory");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to add contact");
    } finally {
      setSaving(false);
    }
  };

  const openContactEditor = (contact) => {
    setEditingContactId(contact.id);
    setForm({ name: contact.name, role: contact.role, phone: contact.phone || "", profile_picture: contact.profile_picture || "" });
  };

  const selectContactPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const profilePicture = await readImageFile(file);
      setForm((current) => ({ ...current, profile_picture: profilePicture }));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const removeContact = async (contact) => {
    if (!window.confirm(`Remove ${contact.name} from the directory?`)) return;
    try {
      await api.delete(`/org-chart/contacts/${contact.id}`);
      setContacts((current) => current.filter((entry) => entry.id !== contact.id));
      toast.success("Contact removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to remove contact");
    }
  };

  const addFacility = async (event) => {
    event.preventDefault();
    if (!facilityForm.name.trim() || !facilityForm.location.trim()) {
      toast.error("Facility name and location are required");
      return;
    }
    setSavingFacility(true);
    try {
      const payload = {
        name: facilityForm.name.trim(),
        location: facilityForm.location.trim(),
        description: facilityForm.description.trim(),
        photos: facilityForm.photos,
      };
      const response = editingFacilityId
        ? await api.put(`/org-chart/facilities/${editingFacilityId}`, payload)
        : await api.post("/org-chart/facilities", payload);
      setFacilities((current) => editingFacilityId
        ? current.map((entry) => entry.id === editingFacilityId ? response.data : entry)
        : [response.data, ...current]);
      setFacilityForm(EMPTY_FACILITY);
      setEditingFacilityId(null);
      toast.success(editingFacilityId ? "Facility updated" : "Facility added to the directory");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to add facility");
    } finally {
      setSavingFacility(false);
    }
  };

  const openFacilityEditor = (facility) => {
    setEditingFacilityId(facility.id);
    setFacilityForm({
      name: facility.name,
      location: facility.location,
      description: facility.description || "",
      photos: facility.photos || [],
    });
  };

  const selectFacilityPhotos = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    const availableSlots = 3 - facilityForm.photos.length;
    if (selectedFiles.length > availableSlots) {
      toast.error(`You can add up to 3 photos per facility`);
    }
    const filesToRead = selectedFiles.slice(0, availableSlots);
    const photos = [];
    for (const file of filesToRead) {
      try {
        photos.push(await readImageFile(file));
      } catch (error) {
        toast.error(error.message);
      }
    }
    if (photos.length) {
      setFacilityForm((current) => ({ ...current, photos: [...current.photos, ...photos] }));
    }
  };

  const removeFacility = async (facility) => {
    if (!window.confirm(`Remove ${facility.name} from the directory?`)) return;
    try {
      await api.delete(`/org-chart/facilities/${facility.id}`);
      setFacilities((current) => current.filter((entry) => entry.id !== facility.id));
      toast.success("Facility removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to remove facility");
    }
  };

  const openPolicyEditor = (policySection, entry = null) => {
    setPolicyEditing({ section: policySection, id: entry?.id || null });
    setPolicyDraft({ category: entry?.category || "", content: entry?.content || "", photos: entry?.photos || [] });
  };

  const selectWorkflowPhotos = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    const availableSlots = 3 - policyDraft.photos.length;
    if (selectedFiles.length > availableSlots) toast.error("You can add up to 3 photos per workflow entry");
    const photos = [];
    for (const file of selectedFiles.slice(0, availableSlots)) {
      try {
        photos.push(await readImageFile(file));
      } catch (error) {
        toast.error(error.message);
      }
    }
    if (photos.length) setPolicyDraft((current) => ({ ...current, photos: [...current.photos, ...photos] }));
  };

  const savePolicyEntries = async (nextPolicies, successMessage) => {
    setPolicySaving(true);
    try {
      const response = await api.put("/rules-responsibilities", nextPolicies);
      setPolicies({
        rules: response.data?.rules || [],
        workscope: response.data?.workscope || response.data?.responsibilities || [],
        workflow: response.data?.workflow || [],
        vision: response.data?.vision || "",
        mission: response.data?.mission || "",
      });
      toast.success(successMessage);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save directory content");
      return false;
    } finally {
      setPolicySaving(false);
    }
  };

  const saveVisionMission = async (event) => {
    event.preventDefault();
    const hasText = (content) => content.replace(/<[^>]*>/g, "").replace(/&nbsp;|&#160;/gi, " ").trim();
    if (!hasText(visionMissionDraft.vision) || !hasText(visionMissionDraft.mission)) {
      toast.error("Add both the hostel vision and mission before saving");
      return;
    }
    const saved = await savePolicyEntries({ ...policies, ...visionMissionDraft }, "Vision and mission updated");
    if (saved) setVisionMissionEditing(false);
  };

  const submitPolicyEntry = async (event) => {
    event.preventDefault();
    const category = policyDraft.category.trim();
    if (!category || !policyDraft.content.replace(/<[^>]*>/g, "").trim()) {
      toast.error("Enter a category and content");
      return;
    }
    const currentEntries = policies[policyEditing.section];
    if (policyEditing.section === "workscope" && !policyEditing.id && currentEntries.some((entry) => entry.category === category)) {
      toast.error("A workscope entry already exists for this role. Edit the existing entry instead.");
      return;
    }
    const nextEntry = { id: policyEditing.id || undefined, category, content: policyDraft.content, photos: policyDraft.photos };
    const nextEntries = policyEditing.id
      ? currentEntries.map((entry) => entry.id === policyEditing.id ? { ...entry, ...nextEntry } : entry)
      : [...currentEntries, nextEntry];
    const saved = await savePolicyEntries({ ...policies, [policyEditing.section]: nextEntries }, policyEditing.id ? "Entry updated" : "Entry added");
    if (saved) setPolicyEditing(null);
  };

  const deletePolicyEntry = async (policySection, entry) => {
    if (!window.confirm(`Delete the “${entry.category}” entry?`)) return;
    const nextEntries = policies[policySection].filter((item) => item.id !== entry.id);
    await savePolicyEntries({ ...policies, [policySection]: nextEntries }, "Entry deleted");
  };

  const startRosterEdit = () => {
    setRosterDraft(JSON.parse(JSON.stringify(roster)));
    setRosterEditing(true);
  };
  const cancelRosterEdit = () => { setRosterEditing(false); setRosterDraft(null); };
  const setRosterTitle = (value) => setRosterDraft((d) => ({ ...d, title: value }));
  const setRosterColumn = (index, value) => setRosterDraft((d) => {
    const columns = [...d.columns];
    columns[index] = value;
    return { ...d, columns };
  });
  const setRosterCell = (rowIndex, cellIndex, value) => setRosterDraft((d) => {
    const rows = d.rows.map((r) => ({ ...r, cells: [...r.cells] }));
    rows[rowIndex].cells[cellIndex] = value;
    return { ...d, rows };
  });
  const saveRoster = async () => {
    setRosterSaving(true);
    try {
      const response = await api.put("/roster", {
        title: rosterDraft.title,
        columns: rosterDraft.columns,
        rows: rosterDraft.rows.map((r) => ({ day: r.day, cells: r.cells })),
      });
      setRoster(response.data);
      setRosterEditing(false);
      setRosterDraft(null);
      toast.success("Warden roster updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update roster");
    } finally {
      setRosterSaving(false);
    }
  };

  const normalizedSearch = search.trim().toLowerCase();
  const activeSection = section === "roster" && !roster ? "contacts" : section;
  const visibleContacts = contacts.filter((contact) => (
    contact.role?.toLowerCase() !== "visitor" &&
    `${contact.name} ${contact.role} ${contact.phone}`.toLowerCase().includes(normalizedSearch)
  ));

  return (
    <div className="space-y-8" data-testid="org-chart-page">
      <header className="flex flex-col gap-3 border-b border-border pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
            <ContactRound className="h-4 w-4" /> Hostel Directory
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold">Hostel Directory</h1>
          <p className="mt-1 text-sm text-muted-foreground">Registered staff and emergency contacts</p>
        </div>
      </header>

      <Tabs value={activeSection} onValueChange={(value) => {
        if (rosterEditing && value !== "roster") cancelRosterEdit();
        setSection(value);
      }}>
        <TabsList className="h-auto max-w-full flex-wrap justify-start gap-1" data-testid="org-chart-sections">
          {roster && <TabsTrigger value="roster" data-testid="org-chart-roster-tab">Warden Duty Roster</TabsTrigger>}
          <TabsTrigger value="org-chart" data-testid="org-chart-structure-tab">Org Chart</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="org-chart-contacts-tab">Contact Directory</TabsTrigger>
          <TabsTrigger value="facilities" data-testid="org-chart-facilities-tab">Facilities</TabsTrigger>
          <TabsTrigger value="vision-mission" data-testid="org-chart-vision-mission-tab">Vision &amp; Mission</TabsTrigger>
          <TabsTrigger value="birthdays" data-testid="org-chart-birthdays-tab">Birthdays</TabsTrigger>
          {POLICY_SECTIONS.map((policySection) => <TabsTrigger key={policySection.id} value={policySection.id} data-testid={`org-chart-${policySection.id}-tab`}>{policySection.title}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {activeSection === "vision-mission" && (
        <section className="space-y-5 border-t-4 border-emerald-600 pt-4" aria-labelledby="vision-mission-heading" data-testid="hostel-vision-mission-section">
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-4">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-800"><Compass className="h-4 w-4" /> Direction</p>
              <h2 id="vision-mission-heading" className="mt-1 font-display text-2xl font-extrabold">Vision &amp; Mission</h2>
            </div>
            {canManageContacts && !visionMissionEditing && (
              <Button type="button" onClick={() => {
                setVisionMissionDraft({ vision: policies.vision, mission: policies.mission });
                setVisionMissionEditing(true);
              }} disabled={loading || policySaving} data-testid="vision-mission-edit-button">
                <Pencil className="h-4 w-4" /> Edit vision &amp; mission
              </Button>
            )}
          </div>

          {visionMissionEditing ? (
            <form onSubmit={saveVisionMission} className="space-y-4" data-testid="vision-mission-form">
              <div className="grid gap-4 lg:grid-cols-2">
                <section className="space-y-3 border-t-4 border-emerald-600 bg-emerald-50/70 p-4" aria-labelledby="vision-editor-heading">
                  <h3 id="vision-editor-heading" className="flex items-center gap-2 font-display text-lg font-bold text-emerald-900"><Compass className="h-5 w-5" /> Vision</h3>
                  <RichTextEditor value={visionMissionDraft.vision} onChange={(vision) => setVisionMissionDraft((current) => ({ ...current, vision }))} ariaLabel="Hostel vision" />
                </section>
                <section className="space-y-3 border-t-4 border-rose-600 bg-rose-50/70 p-4" aria-labelledby="mission-editor-heading">
                  <h3 id="mission-editor-heading" className="flex items-center gap-2 font-display text-lg font-bold text-rose-900"><Target className="h-5 w-5" /> Mission</h3>
                  <RichTextEditor value={visionMissionDraft.mission} onChange={(mission) => setVisionMissionDraft((current) => ({ ...current, mission }))} ariaLabel="Hostel mission" />
                </section>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setVisionMissionEditing(false)} disabled={policySaving}><X className="h-4 w-4" /> Cancel</Button>
                <Button type="submit" disabled={policySaving}><Save className="h-4 w-4" /> {policySaving ? "Saving…" : "Save"}</Button>
              </div>
            </form>
          ) : loading ? (
            <p className="py-5 text-sm text-muted-foreground">Loading vision and mission…</p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <article className="min-h-48 border-t-4 border-emerald-600 bg-emerald-50/70 p-5" data-testid="hostel-vision-panel">
                <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-emerald-900"><Compass className="h-5 w-5" /> Vision</h3>
                {policies.vision ? <RichTextEditor readOnly value={policies.vision} ariaLabel="Hostel vision" /> : <p className="text-sm text-muted-foreground">Vision has not been added yet.</p>}
              </article>
              <article className="min-h-48 border-t-4 border-rose-600 bg-rose-50/70 p-5" data-testid="hostel-mission-panel">
                <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-rose-900"><Target className="h-5 w-5" /> Mission</h3>
                {policies.mission ? <RichTextEditor readOnly value={policies.mission} ariaLabel="Hostel mission" /> : <p className="text-sm text-muted-foreground">Mission has not been added yet.</p>}
              </article>
            </div>
          )}
        </section>
      )}

      {activeSection === "roster" && roster && (
        <section className="space-y-4 border-t-4 border-sky-500 border-b border-border pb-6 pt-4" aria-labelledby="roster-heading" data-testid="warden-roster-section">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
                <CalendarClock className="h-4 w-4" /> Warden Duty Roster
              </p>
              {rosterEditing ? (
                <Input value={rosterDraft.title} onChange={(event) => setRosterTitle(event.target.value)} className="mt-2 font-display font-bold" data-testid="roster-title-input" />
              ) : (
                <h2 id="roster-heading" className="mt-1 font-display text-2xl font-extrabold" data-testid="roster-title">{roster.title}</h2>
              )}
            </div>
            {canManageContacts && !rosterEditing && (
              <Button onClick={startRosterEdit} data-testid="roster-edit-btn"><Pencil className="h-4 w-4" /> Edit roster</Button>
            )}
            {canManageContacts && rosterEditing && (
              <div className="flex gap-2">
                <Button onClick={saveRoster} disabled={rosterSaving} data-testid="roster-save-btn"><Save className="h-4 w-4" /> {rosterSaving ? "Saving…" : "Save"}</Button>
                <Button variant="secondary" onClick={cancelRosterEdit} data-testid="roster-cancel-btn"><X className="h-4 w-4" /> Cancel</Button>
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="roster-table">
              <thead>
                <tr>
                  <th className="border border-border bg-secondary px-3 py-3 text-left font-bold w-32">HARI / MASA</th>
                  {(rosterEditing ? rosterDraft : roster).columns.map((col, i) => (
                    <th key={i} className="border border-border bg-secondary px-3 py-3 text-center font-bold align-middle">
                      {rosterEditing ? (
                        <Input value={col} onChange={(event) => setRosterColumn(i, event.target.value)} className="h-9 text-center" data-testid={`roster-col-${i}`} />
                      ) : col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(rosterEditing ? rosterDraft : roster).rows.map((row, ri) => (
                  <tr key={row.day}>
                    <td className={`border border-border px-3 py-3 font-bold ${DAY_STYLE[row.day] || "bg-secondary"}`} data-testid={`roster-day-${row.day}`}>{row.day}</td>
                    {row.cells.map((cell, ci) => (
                      <td key={ci} className="border border-border px-3 py-2 text-center align-top">
                        {rosterEditing ? (
                          <Textarea value={cell} onChange={(event) => setRosterCell(ri, ci, event.target.value)} rows={2} className="min-h-0 resize-y text-center" data-testid={`roster-cell-${ri}-${ci}`} />
                        ) : (
                          <span className="whitespace-pre-line font-medium">{cell.split(",").map((n) => n.trim()).filter(Boolean).join("\n")}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canManageContacts && !rosterEditing && (
            <p className="text-xs text-muted-foreground">Admins and Hostel Supervisors can edit warden names and time slots using the Edit button.</p>
          )}
        </section>
      )}

      {activeSection === "org-chart" && <OrgStructureChart />}
      {activeSection === "contacts" && <div className="flex justify-end">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, role or phone"
            aria-label="Search contacts"
            className="pl-9"
          />
        </div>
      </div>}

      {activeSection === "contacts" && <section className="space-y-4 border-t-4 border-emerald-500 pt-4" aria-labelledby="directory-heading">
        <div className="flex items-center justify-between gap-3">
          <h2 id="directory-heading" className="font-display text-xl font-bold">Contact directory</h2>
          <Badge variant="secondary">{visibleContacts.length} contacts</Badge>
        </div>

        {canManageContacts && (
          <form onSubmit={addContact} className="grid gap-3 border-y border-border py-4 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_auto] xl:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Name</Label>
              <Input id="contact-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="e.g. Aisha Rahman" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-role">Role or service</Label>
              <Input id="contact-role" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} placeholder="e.g. Medical Officer" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Contact number</Label>
              <Input id="contact-phone" type="tel" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="Phone number" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-photo">Photo (optional)</Label>
              <Input id="contact-photo" type="file" accept="image/*" onChange={selectContactPhoto} className="max-w-sm" />
              {form.profile_picture && <div className="flex items-center gap-2 text-xs text-muted-foreground"><img src={form.profile_picture} alt="Contact photo preview" className="h-9 w-9 rounded-full object-cover" /><button type="button" onClick={() => setForm((current) => ({ ...current, profile_picture: "" }))} className="underline">Remove photo</button></div>}
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={saving}>
                {editingContactId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {saving ? "Saving…" : editingContactId ? "Save contact" : "Add contact"}
              </Button>
              {editingContactId && <Button type="button" variant="outline" onClick={() => { setEditingContactId(null); setForm(emptyForm); }}>Cancel</Button>}
            </div>
          </form>
        )}

        {loading ? (
          <p className="border-y border-border py-8 text-center text-sm text-muted-foreground">Loading contacts…</p>
        ) : visibleContacts.length === 0 ? (
          <p className="border-y border-border py-8 text-center text-sm text-muted-foreground">
            {search ? "No contacts match your search." : "No contacts are listed yet."}
          </p>
        ) : (
          <div className="divide-y divide-border border-y border-border">
            {visibleContacts.map((contact) => (
              <article key={`${contact.type}-${contact.id}`} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`directory-contact-${contact.id}`}>
                <div className="flex min-w-0 items-start gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary">
                    {contact.profile_picture ? (
                      <img src={contact.profile_picture} alt={`${contact.name} profile`} className="h-full w-full object-cover" />
                    ) : (
                      <UserRound className="h-5 w-5" />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{contact.name}</p>
                    <p className="text-sm text-muted-foreground">{contact.role}</p>
                    <Badge variant="outline" className="mt-1 text-[10px] font-normal">
                      {contact.type === "registered" ? "Registered user" : "External contact"}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 pl-[52px] sm:pl-0">
                  {contact.phone ? (
                    <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
                      <Phone className="h-4 w-4" /> {contact.phone}
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground">No number listed</span>
                  )}
                  {canManageContacts && contact.type === "external" && (
                    <div className="flex items-center">
                      <Button variant="ghost" size="icon" onClick={() => openContactEditor(contact)} aria-label={`Edit ${contact.name}`} title="Edit contact">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => removeContact(contact)} aria-label={`Remove ${contact.name}`} title="Remove contact">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {user?.role === "admin" && contact.type === "registered" && (
                    <>
                      <input
                        id={`contact-photo-${contact.id}`}
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(event) => updateRegisteredPhoto(event, contact)}
                      />
                      <label htmlFor={`contact-photo-${contact.id}`} className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary hover:underline">
                        <Camera className="h-4 w-4" /> {contact.profile_picture ? "Replace photo" : "Add photo"}
                      </label>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>}

      {activeSection === "facilities" && <section className="space-y-4 border-t-4 border-amber-500 pt-4" aria-labelledby="facilities-heading" data-testid="hostel-facilities-section">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary">
              <MapPin className="h-4 w-4" /> Places & resources
            </p>
            <h2 id="facilities-heading" className="mt-1 font-display text-xl font-bold">Facilities directory</h2>
          </div>
          <Badge variant="secondary">{facilities.length} locations</Badge>
        </div>

        {canManageContacts && (
          <form onSubmit={addFacility} className="grid gap-3 border-y border-border py-4 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_auto] xl:items-end" data-testid="hostel-facility-form">
            <div className="space-y-1.5">
              <Label htmlFor="facility-name">Facility</Label>
              <Input id="facility-name" value={facilityForm.name} onChange={(event) => setFacilityForm({ ...facilityForm, name: event.target.value })} placeholder="e.g. First aid kit" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="facility-location">Location</Label>
              <Input id="facility-location" value={facilityForm.location} onChange={(event) => setFacilityForm({ ...facilityForm, location: event.target.value })} placeholder="e.g. Main office, ground floor" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="facility-description">Notes</Label>
              <Input id="facility-description" value={facilityForm.description} onChange={(event) => setFacilityForm({ ...facilityForm, description: event.target.value })} placeholder="Optional details" />
            </div>
            <div className="space-y-2 sm:col-span-2 xl:col-span-4">
              <Label htmlFor="facility-photos">Photos (up to 3)</Label>
              <div className="flex flex-wrap items-center gap-3">
                <Input id="facility-photos" type="file" accept="image/*" multiple disabled={facilityForm.photos.length >= 3} onChange={selectFacilityPhotos} className="max-w-sm" data-testid="hostel-facility-photos-input" />
                <span className="text-xs text-muted-foreground">{facilityForm.photos.length}/3 selected; max 3 MB each</span>
              </div>
              {facilityForm.photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {facilityForm.photos.map((photo, index) => (
                    <div key={`${index}-${photo.slice(-12)}`} className="relative h-20 w-24 overflow-hidden rounded border border-border">
                      <img src={photo} alt={`Facility preview ${index + 1}`} className="h-full w-full object-cover" />
                      <button type="button" onClick={() => setFacilityForm((current) => ({ ...current, photos: current.photos.filter((_, photoIndex) => photoIndex !== index) }))} className="absolute right-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-xs text-white" aria-label={`Remove photo ${index + 1}`}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={savingFacility} data-testid="hostel-facility-add-button">
                {editingFacilityId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {savingFacility ? "Saving…" : editingFacilityId ? "Save facility" : "Add facility"}
              </Button>
              {editingFacilityId && <Button type="button" variant="outline" onClick={() => { setEditingFacilityId(null); setFacilityForm(EMPTY_FACILITY); }}>Cancel</Button>}
            </div>
          </form>
        )}

        {facilities.length === 0 ? (
          <p className="border-y border-border py-8 text-center text-sm text-muted-foreground">No facilities are listed yet.</p>
        ) : (
          <div className="divide-y divide-border border-y border-border">
            {facilities.map((facility) => (
              <article key={facility.id} className="flex items-start justify-between gap-4 py-4" data-testid={`hostel-facility-${facility.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold">{facility.name}</p>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5 shrink-0" />{facility.location}</p>
                  {facility.description && <p className="mt-1 text-sm text-muted-foreground">{facility.description}</p>}
                  {facility.photos?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {facility.photos.map((photo, index) => <img key={`${facility.id}-photo-${index}`} src={photo} alt={`${facility.name} photo ${index + 1}`} className="h-20 w-28 rounded border border-border object-cover" />)}
                    </div>
                  )}
                </div>
                {canManageContacts && (
                  <div className="flex shrink-0 items-center">
                    <Button variant="ghost" size="icon" onClick={() => openFacilityEditor(facility)} aria-label={`Edit ${facility.name}`} title="Edit facility">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeFacility(facility)} aria-label={`Remove ${facility.name}`} title="Remove facility">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>}

      {activeSection === "birthdays" && <section className="space-y-4 border-t-4 border-rose-500 pt-4" aria-labelledby="birthdays-heading">
        <div className="flex items-center gap-2">
          <Cake className="h-5 w-5 text-orange-600" />
          <h2 id="birthdays-heading" className="font-display text-xl font-bold">Birthdays in {monthName}</h2>
        </div>
        {birthdays.length === 0 ? (
          <p className="border-y border-border py-5 text-sm text-muted-foreground">No birthdays this month.</p>
        ) : (
          <div className="divide-y divide-border border-y border-border">
            {birthdays.map((birthday) => (
              <div key={birthday.id} className="flex items-center justify-between gap-4 py-3" data-testid={`birthday-${birthday.id}`}>
                <div>
                  <p className="font-semibold">{birthday.name}</p>
                  <p className="text-sm text-muted-foreground">{monthName} {birthday.day}</p>
                </div>
                <Button size="sm" variant="ghost" className="text-orange-700" data-testid={`wish-btn-${birthday.id}`} onClick={() => toast.success(`Birthday wish sent to ${birthday.name}!`)}>
                  Wish them well
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>}

      {POLICY_SECTIONS.map(({ id, title, empty }) => {
        if (activeSection !== id) return null;
        const categories = id === "rules"
          ? RULE_CATEGORIES
          : id === "workscope"
            ? WORKSCOPE_ROLES
            : [...new Set(policies[id].map((entry) => entry.category))].sort((a, b) => a.localeCompare(b));
        const visibleEntries = policies[id].filter((entry) => policyFilters[id] === "all" || entry.category === policyFilters[id]);
        return (
          <section key={id} className="space-y-4 border-t-4 border-sky-500 pt-4" aria-labelledby={`${id}-heading`} data-testid={`org-chart-${id}-section`}>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 id={`${id}-heading`} className="font-display text-xl font-bold">{title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{policies[id].length} {policies[id].length === 1 ? "entry" : "entries"}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={policyFilters[id]}
                  onChange={(event) => setPolicyFilters((current) => ({ ...current, [id]: event.target.value }))}
                  className="h-9 max-w-48 rounded-md border border-input bg-background px-3 text-sm"
                  aria-label={`Filter ${title} by category`}
                >
                  <option value="all">All categories</option>
                  {categories.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
                {canManageContacts && <Button type="button" size="sm" onClick={() => openPolicyEditor(id)} disabled={loading || policySaving} data-testid={`org-chart-add-${id}`}><Plus className="h-4 w-4" />Add entry</Button>}
              </div>
            </div>
            {loading ? <p className="py-5 text-sm text-muted-foreground">Loading…</p> : visibleEntries.length === 0 ? (
              <p className="border-y border-border py-5 text-sm text-muted-foreground">{policyFilters[id] === "all" ? empty : "No entries in this category."}</p>
            ) : (
              <div className="divide-y divide-border border-y border-border">
                {visibleEntries.map((entry) => (
                  <article key={entry.id} className="py-4" data-testid={`org-chart-${id}-entry-${entry.id}`}>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <Badge variant="secondary">{entry.category}</Badge>
                      {canManageContacts && (
                        <div className="flex items-center gap-1">
                          <Button type="button" size="icon" variant="ghost" aria-label={`Edit ${entry.category}`} title="Edit entry" onClick={() => openPolicyEditor(id, entry)} disabled={policySaving}><Pencil className="h-4 w-4" /></Button>
                          <Button type="button" size="icon" variant="ghost" aria-label={`Delete ${entry.category}`} title="Delete entry" onClick={() => deletePolicyEntry(id, entry)} disabled={policySaving}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      )}
                    </div>
                    <RichTextEditor readOnly value={entry.content} ariaLabel={`${entry.category} content`} />
                    {id === "workflow" && entry.photos?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {entry.photos.map((photo, index) => <img key={`${entry.id}-photo-${index}`} src={photo} alt={`${entry.category} workflow photo ${index + 1}`} className="h-24 w-32 rounded border border-border object-cover" />)}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        );
      })}

      <Dialog open={Boolean(policyEditing)} onOpenChange={(open) => { if (!open) setPolicyEditing(null); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{policyEditing?.id ? "Edit entry" : "Add entry"}</DialogTitle>
            <DialogDescription>Choose a category and format the section content.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPolicyEntry} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="directory-policy-category">{policyEditing?.section === "workscope" ? "Employee role" : "Category"}</Label>
              {policyEditing?.section === "rules" || policyEditing?.section === "workscope" ? (
                <select
                  id="directory-policy-category"
                  required
                  value={policyDraft.category}
                  onChange={(event) => setPolicyDraft((current) => ({ ...current, category: event.target.value }))}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  data-testid={policyEditing.section === "rules" ? "directory-rule-category-select" : "directory-workscope-role-select"}
                >
                  <option value="">{policyEditing.section === "workscope" ? "Choose an employee role" : "Choose a rule category"}</option>
                  {(policyEditing.section === "workscope" ? WORKSCOPE_ROLES : RULE_CATEGORIES).map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              ) : (
                <Input id="directory-policy-category" maxLength={80} required value={policyDraft.category} onChange={(event) => setPolicyDraft((current) => ({ ...current, category: event.target.value }))} placeholder="For example, Safety or Attendance" />
              )}
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <RichTextEditor value={policyDraft.content} onChange={(content) => setPolicyDraft((current) => ({ ...current, content }))} ariaLabel="Directory section content" />
            </div>
            {policyEditing?.section === "workflow" && (
              <div className="space-y-2">
                <Label htmlFor="workflow-photos">Pictures (up to 3, max 3 MB each)</Label>
                <Input id="workflow-photos" type="file" accept="image/*" multiple disabled={policyDraft.photos.length >= 3} onChange={selectWorkflowPhotos} />
                <p className="text-xs text-muted-foreground">{policyDraft.photos.length}/3 selected</p>
                {policyDraft.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {policyDraft.photos.map((photo, index) => (
                      <div key={`${index}-${photo.slice(-12)}`} className="relative h-20 w-24 overflow-hidden rounded border border-border">
                        <img src={photo} alt={`Workflow preview ${index + 1}`} className="h-full w-full object-cover" />
                        <button type="button" onClick={() => setPolicyDraft((current) => ({ ...current, photos: current.photos.filter((_, photoIndex) => photoIndex !== index) }))} className="absolute right-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-xs text-white" aria-label={`Remove workflow picture ${index + 1}`}><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPolicyEditing(null)}>Cancel</Button>
              <Button type="submit" disabled={policySaving}>{policySaving ? "Saving…" : "Save entry"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}